import React, { useState } from "react";
import { toast } from "sonner";
import { StickyNote, Save, Plus, UserCog, Pencil, Trash2, Paperclip } from "lucide-react";

// Account Details & Notes for a loan file.
// - Fixed profile fields (employer, income, contact prefs, etc.)
// - Running internal notes thread (broker <-> admin), newest first.
// editable: Admin/Broker can edit fields + add notes. Borrower is read-only
// (and the notes thread is stripped server-side for borrowers).

export const ACCOUNT_FIELDS = [
  { k: "employer", label: "Employer" },
  { k: "employment_status", label: "Employment Status" },
  { k: "job_title", label: "Job Title" },
  { k: "years_employed", label: "Years Employed" },
  { k: "annual_income", label: "Annual Income" },
  { k: "monthly_income", label: "Monthly Income" },
  { k: "other_income", label: "Other Income" },
  { k: "credit_score", label: "Credit Score" },
  { k: "occupancy", label: "Occupancy (Owner / Rental / Vacant)" },
  { k: "marital_status", label: "Marital Status" },
  { k: "dependents", label: "Dependents" },
  { k: "best_contact_phone", label: "Best Contact Phone" },
  { k: "best_contact_email", label: "Best Contact Email" },
  { k: "preferred_contact_method", label: "Preferred Contact Method" },
  { k: "best_time_to_contact", label: "Best Time to Contact" },
  { k: "mailing_address", label: "Mailing Address" },
  { k: "property_condition", label: "Property Condition" },
  { k: "hardship_reason", label: "Hardship / Reason for Refinance" },
  { k: "bankruptcy_status", label: "Bankruptcy / Legal Status" },
];

const roleBadge = (role) => {
  const r = String(role || "").toLowerCase();
  if (r === "admin") return { label: "Admin", cls: "bg-amber-100 text-amber-800 border-amber-300" };
  if (r === "broker") return { label: "Broker", cls: "bg-blue-100 text-blue-800 border-blue-300" };
  return { label: "Borrower", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" };
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; }
};

export const AccountDetailsPanel = ({ application, editable = false, showNotes = false, theme = "light", actorRole = "admin", onSaveProfile, onAddNote, onEditNote, onDeleteNote, onAttach, attachmentUrl }) => {
  const [form, setForm] = useState({ ...(application?.account_profile || {}) });
  const [notes, setNotes] = useState(application?.account_notes || []);
  const [noteText, setNoteText] = useState("");
  const [savingP, setSavingP] = useState(false);
  const [savingN, setSavingN] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [busyId, setBusyId] = useState(null);

  const canMod = (n) => editable && (actorRole === "admin" || n.author_role === actorRole);

  const startEdit = (n) => { setEditingId(n.id); setEditText(n.text || ""); };
  const saveEdit = async (n) => {
    if (!onEditNote) return; setBusyId(n.id);
    try { const arr = await onEditNote(n.id, editText.trim()); if (Array.isArray(arr)) setNotes(arr); setEditingId(null); toast.success("Note updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update note"); }
    finally { setBusyId(null); }
  };
  const removeNote = async (n) => {
    if (!onDeleteNote || !window.confirm("Delete this note?")) return; setBusyId(n.id);
    try { const arr = await onDeleteNote(n.id); if (Array.isArray(arr)) setNotes(arr); toast.success("Note deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete note"); }
    finally { setBusyId(null); }
  };
  const attachFile = async (n, file) => {
    if (!onAttach || !file) return; setBusyId(n.id);
    try { const arr = await onAttach(n.id, file); if (Array.isArray(arr)) setNotes(arr); toast.success("File attached"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not attach file"); }
    finally { setBusyId(null); }
  };

  const dark = theme === "dark";
  const cardCls = dark ? "bg-neutral-900/60 border-neutral-700 text-neutral-100" : "bg-white border-neutral-200 text-neutral-900";
  const labelCls = dark ? "text-neutral-400" : "text-neutral-500";
  const inputCls = dark
    ? "w-full bg-neutral-800 border border-neutral-600 rounded-md px-2.5 py-1.5 text-sm text-neutral-100 focus:outline-none focus:ring-1 focus:ring-amber-400"
    : "w-full bg-white border border-neutral-300 rounded-md px-2.5 py-1.5 text-sm text-neutral-900 focus:outline-none focus:ring-1 focus:ring-blue-400";
  const valCls = dark ? "text-neutral-100" : "text-neutral-800";

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const saveProfile = async () => {
    if (!onSaveProfile) return;
    setSavingP(true);
    try { await onSaveProfile(form); toast.success("Account details saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save account details"); }
    finally { setSavingP(false); }
  };

  const addNote = async () => {
    if (!onAddNote || !noteText.trim()) return;
    setSavingN(true);
    try {
      const arr = await onAddNote(noteText.trim());
      if (Array.isArray(arr)) setNotes(arr);
      setNoteText(""); toast.success("Note added");
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add note"); }
    finally { setSavingN(false); }
  };

  return (
    <div className={`rounded-xl border p-4 sm:p-5 ${cardCls}`} data-testid="account-details-panel">
      <div className="flex items-center gap-2 mb-3">
        <UserCog className="w-4 h-4 opacity-70" />
        <h3 className="text-base font-semibold">Account Details {editable ? "" : "(read-only)"}</h3>
        {(application?.last_edited_at || application?.updated_at || application?.created_at) && (
          <span className="ml-auto text-[11px] opacity-70" data-testid="account-last-updated">
            Last updated: {fmtDate(application.last_edited_at || application.updated_at || application.created_at)}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        {ACCOUNT_FIELDS.map(({ k, label }) => (
          <div key={k} className={k === "mailing_address" || k === "hardship_reason" ? "sm:col-span-2" : ""}>
            <div className={`text-xs mb-0.5 ${labelCls}`}>{label}</div>
            {editable ? (
              <input className={inputCls} value={form[k] || ""} onChange={set(k)} data-testid={`account-field-${k}`} placeholder="—" />
            ) : (
              <div className={`text-sm ${valCls}`} data-testid={`account-view-${k}`}>{form[k] || <span className="opacity-40">—</span>}</div>
            )}
          </div>
        ))}
        <div className="sm:col-span-2">
          <div className={`text-xs mb-0.5 ${labelCls}`}>Additional Details</div>
          {editable ? (
            <textarea className={`${inputCls} min-h-[70px]`} value={form.additional_details || ""} onChange={set("additional_details")} data-testid="account-field-additional_details" placeholder="Any extra detail about this borrower's account…" />
          ) : (
            <div className={`text-sm whitespace-pre-wrap ${valCls}`} data-testid="account-view-additional_details">{form.additional_details || <span className="opacity-40">—</span>}</div>
          )}
        </div>
      </div>

      {editable && (
        <div className="mt-4">
          <button onClick={saveProfile} disabled={savingP} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-3.5 py-2 disabled:opacity-60" data-testid="account-save-profile-btn">
            <Save className="w-4 h-4" /> {savingP ? "Saving…" : "Save Account Details"}
          </button>
        </div>
      )}

      {showNotes && (
        <div className={`mt-6 pt-4 border-t ${dark ? "border-neutral-700" : "border-neutral-200"}`}>
          <div className="flex items-center gap-2 mb-3">
            <StickyNote className="w-4 h-4 opacity-70" />
            <h3 className="text-base font-semibold">Account Notes <span className="text-xs font-normal opacity-60">· internal (Broker &amp; Admin only)</span></h3>
          </div>

          {editable && (
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <textarea className={`${inputCls} min-h-[54px] flex-1`} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a detailed note about this borrower's account…" data-testid="account-note-input" />
              <button onClick={addNote} disabled={savingN || !noteText.trim()} className="inline-flex items-center justify-center gap-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-3.5 py-2 disabled:opacity-60 h-fit self-end" data-testid="account-add-note-btn">
                <Plus className="w-4 h-4" /> {savingN ? "Adding…" : "Add Note"}
              </button>
            </div>
          )}

          <div className="space-y-2.5" data-testid="account-notes-list">
            {notes.length === 0 && <div className="text-sm opacity-50">No notes yet.</div>}
            {notes.map((n) => {
              const b = roleBadge(n.author_role);
              const editingThis = editingId === n.id;
              return (
                <div key={n.id} className={`rounded-lg border p-3 ${dark ? "border-neutral-700 bg-neutral-800/50" : "border-neutral-200 bg-neutral-50"}`} data-testid={`account-note-${n.id}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${b.cls}`}>{b.label}</span>
                    <span className="text-sm font-medium">{n.author_name}</span>
                    <span className="text-xs opacity-50">{fmtDate(n.created_at)}</span>
                    {n.edited_at && <span className="text-[10px] italic opacity-40">edited {fmtDate(n.edited_at)}</span>}
                    {canMod(n) && !editingThis && (
                      <span className="ml-auto flex items-center gap-2">
                        <label className="cursor-pointer text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1" data-testid={`account-note-attach-${n.id}`}>
                          <Paperclip className="w-3.5 h-3.5" />
                          <input type="file" className="hidden" disabled={busyId === n.id} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) attachFile(n, f); }} />
                        </label>
                        <button onClick={() => startEdit(n)} className="text-xs opacity-70 hover:opacity-100" data-testid={`account-note-edit-${n.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => removeNote(n)} disabled={busyId === n.id} className="text-xs text-red-400 hover:text-red-300" data-testid={`account-note-delete-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </span>
                    )}
                  </div>
                  {editingThis ? (
                    <div className="mt-1">
                      <textarea className={`${inputCls} min-h-[54px]`} value={editText} onChange={(e) => setEditText(e.target.value)} data-testid={`account-note-edit-input-${n.id}`} />
                      <div className="flex gap-2 mt-1.5">
                        <button onClick={() => saveEdit(n)} disabled={busyId === n.id} className="text-xs rounded bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1" data-testid={`account-note-edit-save-${n.id}`}>Save</button>
                        <button onClick={() => setEditingId(null)} className="text-xs rounded bg-neutral-500/40 px-2.5 py-1">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className={`text-sm whitespace-pre-wrap ${valCls}`}>{n.text}</div>
                  )}
                  {(n.attachments || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid={`account-note-attachments-${n.id}`}>
                      {n.attachments.map((att) => (
                        <a key={att.id} href={attachmentUrl ? attachmentUrl(n.id, att.id) : "#"} target="_blank" rel="noreferrer"
                           className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${dark ? "border-neutral-600 bg-neutral-800 hover:bg-neutral-700" : "border-neutral-300 bg-white hover:bg-neutral-50"}`}>
                          <Paperclip className="w-3 h-3" /> {att.filename}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDetailsPanel;
